<script lang="ts">
import PaymentCardIcon from '@/components/icons/PaymentCardIcon.vue'
import { useCurrencyFormatter } from '@/composables/useCurrencyFormatter'
import { useDepositOptions } from '@/composables/useDepositOptions'
import { useCheckoutBasket } from '@/plugins/useCheckoutBasket'
import { usePaymentProvider } from '@/plugins/usePaymentProvider'
import { useSolidgate } from '@/plugins/useSolidgate'
import { useTailwind } from '@/plugins/useTailwind'
import { AnalyticsService } from '@/services/analytics'
import type {
  Deposit,
  DepositBonusOption,
  Nullable,
  PaymentMethod,
  Pence,
} from '@/types'
import { createPaymentError } from '@/utils/checkout-helpers'
import {
  calculateBonusCredit,
  getBonusTierForAmount,
} from '@/utils/deposit-utils'
import {
  PaymentProviders,
  ProductTypes,
  type TProductSku,
} from '@/utils/helper-objects'
import { closeAllModals } from '@/utils/utils'
import PaymentStageSolidgate from '@/views/Checkout/CheckoutStages/PaymentStageSolidgate.vue'
import type { SolidgatePaymentIntentResponse } from '@solidgate/vue-sdk'
import {
  type UseDebounceFnReturn,
  useDebounceFn,
  useStepper,
} from '@vueuse/core'
import { type Component, defineComponent, useTemplateRef } from 'vue'

import DepositOptionCard from './deposit/DepositOptionCard.vue'
import PaymentMethodsPanel from './deposit/PaymentMethodsPanel.vue'
import PersonalisedAmountCard from './deposit/PersonalisedAmountCard.vue'

const PaymentStageSolidgateComponent = PaymentStageSolidgate as Component

export default defineComponent({
  name: 'DepositModal',
  components: {
    DepositOptionCard,
    PersonalisedAmountCard,
    PaymentMethodsPanel,
    PaymentStageSolidgate: PaymentStageSolidgateComponent,
    PaymentCardIcon,
  },
  props: {
    buttonText: {
      type: String,
      default: 'Deposit',
    },
  },
  setup(): Record<string, any> {
    const tailwind = useTailwind()
    const currencyFormatter = useCurrencyFormatter()
    const {
      mount,
      handlePaymentAuthorized,
      handlePaymentError,
      errors: paymentErrors,
      clearErrors: clearPaymentErrors,
    } = usePaymentProvider(PaymentProviders.Solidgate)
    const { updatePaymentIntent } = useSolidgate()
    const stepper = useStepper(['payment', 'success'])

    return {
      isLg: tailwind.isLg,
      checkoutBasket: useCheckoutBasket(),
      mount,
      handlePaymentAuthorized,
      handlePaymentError,
      updatePaymentIntent,
      clearPaymentErrors,
      paymentErrors,
      stepper,
      // Keep refs in parent for reactive Solidgate integration
      paymentStageRef: useTemplateRef<any>('paymentStageRef'),
      googlePayButton: useTemplateRef<HTMLDivElement>('googlePayButton'),
      applePayButton: useTemplateRef<HTMLDivElement>('applePayButton'),
      useDebounceFn,
      ...currencyFormatter,
      // Spread depositOptions to unwrap refs/computed values
      ...useDepositOptions(),
    }
  },
  data() {
    return {
      isPaymentReady: false,
      isAddNewCardOpen: false,
      isOneClickPayOpen: false,
      isPaymentProcessing: false,
      selectedAmount: '',
      paymentIntentData: null as Nullable<SolidgatePaymentIntentResponse>,
      debouncedUpdatePayment: null as Nullable<UseDebounceFnReturn<() => void>>,
      debouncedPendingAmountApply: null as Nullable<
        UseDebounceFnReturn<() => void>
      >,
      isCustomAmountFocused: false,
      selectedOptionSku: null as Nullable<TProductSku>,
      pendingCustomAmount: '',
      lastSubmittedAmount: 0,
      lastSubmittedSku: null as Nullable<TProductSku>,
      isPaymentUpdating: false,
      successSelectedOption: null as Nullable<DepositBonusOption>,
      refreshAfterClose: false,
    }
  },
  computed: {
    selectedDeposit(): Deposit {
      const items = this.checkoutBasket.basket.items
      const defaultDepositOption =
        this.firstCatalogBundle || this.highestCostCatalogBundle

      if (!defaultDepositOption) {
        return {
          __type: 'Deposit' as const,
          sku: 'CUSTOM' as TProductSku,
          amount: 0,
          bonus_credit_percent: 0,
        }
      }

      const defaultDeposit = this.createDepositFromOption(
        defaultDepositOption,
        defaultDepositOption.price_minor ?? 0
      )

      if (items.length === 0) {
        return defaultDeposit
      }

      const firstItem = items[0]
      if (firstItem.type !== ProductTypes.Deposit) {
        return defaultDeposit
      }

      return firstItem.product as Deposit
    },
    paymentButtonLabel(): string {
      const baseLabel = this.buttonText || 'Deposit'
      return this.selectedDeposit?.bonus_credit_percent > 0 &&
        !this.isOneClickPayOpen
        ? `${baseLabel} & Get Bonus`
        : baseLabel
    },
    selectedOption(): DepositBonusOption | null {
      if (!this.selectedDeposit?.sku) {
        return null
      }
      return this.depositAmounts.get(this.selectedDeposit.sku) ?? null
    },
    successBonusPence(): number {
      const deposit = this.selectedDeposit
      const option = this.successSelectedOption ?? this.selectedOption
      if (!deposit || !option) {
        return 0
      }

      if (
        option.type === 'promotion' &&
        option.promotion_details?.match_percentage !== undefined
      ) {
        const matchMultiplier = option.promotion_details.match_percentage / 100
        const uncapped = deposit.amount * matchMultiplier
        const cap = option.promotion_details.max_bonus_minor
        return cap ? Math.min(uncapped, cap) : uncapped
      }

      return deposit.amount * deposit.bonus_credit_percent
    },
    oneClickPay(): Nullable<PaymentMethod> {
      return (
        Object.values(this.$userStore.user?.payment_methods || {}).find(
          (method) => method.one_click_enabled
        ) ?? null
      )
    },
    showAddNewCard(): boolean {
      return (
        !!this.$userStore.user &&
        !this.isPaymentProcessing &&
        this.isAddNewCardOpen &&
        this.isPaymentReady
      )
    },
    showOneClickPay(): boolean {
      return (
        !!this.$userStore.user &&
        !this.isPaymentProcessing &&
        this.isOneClickPayOpen
      )
    },
    paymentForm() {
      return {
        ...this.$userStore.user,
        amount: this.selectedDeposit.amount,
      }
    },
    paymentFormType() {
      if (this.isOneClickPayOpen) {
        return 'one-click-pay'
      }
      return 'add-new-card'
    },
    apmConfig() {
      return {
        applePay: {
          element: this.applePayButton as HTMLDivElement,
          type: 'pay',
        },
        googlePay: {
          element: this.googlePayButton as HTMLDivElement,
          type: 'pay',
        },
      } as const
    },
    currentBonusPercent(): number {
      return this.selectedDeposit.bonus_credit_percent
    },
    bonusTierInfo() {
      const pendingAmount = this.pendingCustomAmount
      if (!pendingAmount || parseInt(pendingAmount) <= 0) {
        return {
          percent: 0,
          nextTier: null,
          remainingPence: 0,
          nextBonusPercent: 0,
        }
      }
      return getBonusTierForAmount(
        parseInt(pendingAmount),
        this.customWalletOption
      )
    },
    showNextTierHint(): boolean {
      return (
        this.currentBonusPercent >= 0 &&
        this.bonusTierInfo.remainingPence > 0 &&
        this.bonusTierInfo.nextBonusPercent > this.currentBonusPercent
      )
    },
    selectedCoinConfig(): { totalCoins: number; silverCoins: number } | null {
      const option = this.depositOptionsWithCoinCount.find(
        (
          opt: DepositBonusOption & {
            coinCount: number
            silverCoinCount: number
            displayAmount: number
            bonusPercent: number
          }
        ) => opt.sku === this.selectedDeposit.sku
      )
      return option
        ? { totalCoins: option.coinCount, silverCoins: option.silverCoinCount }
        : null
    },
  },
  watch: {
    pendingCustomAmount(value: string, old: string) {
      if (
        value !== old &&
        this.selectedOptionSku === this.customWalletOption?.sku &&
        !this.isPaymentUpdating
      ) {
        void this.debouncedPendingAmountApply?.()
      }

      const numericValue = parseFloat(`${value}`) * 100
      const depositAmount = (Number(numericValue) / 100).toFixed(2)
      const previousDepositAmount = (this.selectedDeposit.amount / 100).toFixed(
        2
      )

      if (previousDepositAmount === depositAmount) {
        return
      }

      this.isPaymentReady = false
    },
    isPaymentReady() {
      if (this.paymentStageRef?.updateAPMButtonState && this.isPaymentReady) {
        this.paymentStageRef.updateAPMButtonState(this.isPaymentReady)
      }
    },
    isAddNewCardOpen() {
      if (this.isAddNewCardOpen) {
        const depositModalBottomAnchor = document.getElementById(
          'deposit-modal-bottom-anchor'
        ) as Nullable<HTMLElement>

        if (depositModalBottomAnchor) {
          setTimeout(() => {
            depositModalBottomAnchor.scrollIntoView({
              behavior: 'smooth',
              block: 'end',
            })
          }, 500)
        }
      }
    },
    isOneClickPayOpen() {
      setTimeout(() => {
        this.clearPaymentErrors()
      }, 500)
    },
  },
  async mounted() {
    await this.loadDepositOptions({ force: true })

    const updatePayment = this.updatePayment.bind(this)
    this.debouncedUpdatePayment = useDebounceFn(updatePayment, 750)

    this.debouncedPendingAmountApply = useDebounceFn(() => {
      if (this.selectedOptionSku === this.customWalletOption?.sku) {
        void this.applyCustomAmount()
      }
    }, 1200)
  },
  methods: {
    isOptionSelected(option: DepositBonusOption): boolean {
      return this.selectedOptionSku === option.sku
    },
    createDepositFromOption(
      option: DepositBonusOption,
      customAmount: Pence = 0
    ): Deposit {
      let amount = 0

      if (option.product_type === 'wallet_top_up') {
        amount = customAmount
      } else if (option.type === 'promotion') {
        amount =
          customAmount > 0 ? customAmount : (option.max_deposit_minor ?? 0)
      } else {
        amount = option.price_minor ?? 0
      }

      if (option.type === 'promotion' && amount === 0) {
        amount = option.max_deposit_minor ?? 0
      } else if (amount < 1 && option.type !== 'promotion') {
        amount = Math.max(1, amount)
      }

      let bonusDecimal = 0
      if (
        option.type === 'promotion' &&
        option.promotion_details?.match_percentage !== undefined
      ) {
        bonusDecimal = option.promotion_details.match_percentage / 100
      } else if (option.components?.[0]?.payload?.bonus_multiplier) {
        bonusDecimal = option.components[0].payload.bonus_multiplier - 1
      } else if (option.metadata?.bonus_multiplier) {
        bonusDecimal = option.metadata.bonus_multiplier - 1
      }

      if (
        option.product_type === 'wallet_top_up' &&
        option.components?.[0]?.payload?.bonus_tiers
      ) {
        bonusDecimal = calculateBonusCredit(
          amount,
          this.customWalletOption,
          this.depositAmounts
        )
      }

      const deposit: Deposit = {
        __type: 'Deposit',
        sku: option.sku,
        amount: amount,
        bonus_credit_percent: bonusDecimal,
      }
      return deposit
    },
    resetState() {
      this.checkoutBasket.clearBasket()
      this.isAddNewCardOpen = false
      this.isOneClickPayOpen = false
      this.isPaymentReady = false
      this.isPaymentProcessing = false
      this.paymentIntentData = null
      this.selectedOptionSku = null
      this.pendingCustomAmount = ''
      this.lastSubmittedAmount = 0
      this.lastSubmittedSku = null
      this.isPaymentUpdating = false
      this.stepper.goTo('payment')
    },
    async handleOpenSheetOrModal(openSheetOrModal: () => void) {
      if (this.isApiUnavailable) {
        this.$alertStore.addErrorAlert({
          title: 'Deposit Unavailable',
          message:
            'We are unable to load deposit options at this time. Please try again later or contact support if this issue persists.',
          buttonText: 'OK',
        })
        return
      }

      // Close all other open modals
      closeAllModals()

      if (!this.hasAnyDepositOptions) {
        this.$alertStore.addErrorAlert({
          title: 'Deposit Unavailable',
          message:
            'No deposit options are currently available. Please try again later or contact support if this issue persists.',
          buttonText: 'OK',
        })
        return
      }

      openSheetOrModal()
      this.resetState()

      const pendingReservation = this.visibleDepositOptions.find(
        (opt: DepositBonusOption) => opt.status === 'pending_reservation'
      )
      const defaultSku =
        pendingReservation?.sku ?? this.highestCostCatalogBundle?.sku

      if (!defaultSku) {
        console.error('[DepositModal] No default SKU available')
        return
      }

      if (!this.depositAmounts.has(defaultSku)) {
        console.error('[DepositModal] Default SKU not found:', defaultSku)
        return
      }

      const defaultOption = this.depositAmounts.get(defaultSku)
      const defaultAmount =
        defaultOption?.type === 'promotion'
          ? (defaultOption.promotion_details?.default_deposit_minor ??
            defaultOption.max_deposit_minor ??
            0)
          : (defaultOption?.price_minor ?? 0)

      this.pendingCustomAmount = Math.floor(defaultAmount / 100).toString()

      await this.addToBasket(defaultSku, defaultAmount)
      await this.mountPaymentProvider()

      AnalyticsService.trackBeginCheckout(this.checkoutBasket.basket.items)
    },
    async mountPaymentProvider() {
      // Wait for DOM refs to be available before mounting Solidgate
      await this.$nextTick()

      setTimeout(async () => {
        try {
          await this.mount(ProductTypes.Deposit)
          this.isPaymentReady = true
        } catch (error) {
          console.error(
            `Failed to initialize ${PaymentProviders.Solidgate}:`,
            error
          )
          this.isPaymentReady = false
        }

        if (this.paymentStageRef?.updateAPMButtonState) {
          this.paymentStageRef.updateAPMButtonState(this.isPaymentReady)
        }
      }, 500)
    },
    async handlePaymentResult(payment: any) {
      this.isPaymentProcessing = false

      if (payment && payment.success) {
        if (!payment.data) {
          this.handlePaymentError(
            createPaymentError(
              "Failed to retrieve payment information. The payment was processed but we couldn't confirm your purchase.",
              payment.orderId
            )
          )
          return
        }

        void this.$userStore.fetchDetails(true)

        // Snapshot the option used for this payment before refreshing state
        this.successSelectedOption = this.selectedOption

        // Defer refreshing deposit options/promotions until after success modal is dismissed
        this.refreshAfterClose = true

        this.stepper.goTo('success')
      } else {
        if (payment.data?.error) {
          this.handlePaymentError(
            createPaymentError(payment.data.error, payment.orderId)
          )
        }
      }
    },
    async runPendingRefresh() {
      if (!this.refreshAfterClose) {
        return
      }
      try {
        await this.loadDepositOptions({ force: true })
      } catch (e) {
        console.error('Failed to refresh deposit/promo data after payment:', e)
      } finally {
        this.refreshAfterClose = false
      }
    },
    async updatePayment() {
      if (
        !this.isPaymentReady ||
        !this.paymentIntentData ||
        !this.paymentStageRef ||
        !this.paymentStageRef.formMounted
      ) {
        return
      }

      const currentAmount = this.selectedDeposit.amount
      const currentSku = this.selectedDeposit.sku
      if (
        currentAmount === this.lastSubmittedAmount &&
        currentSku === this.lastSubmittedSku
      ) {
        return
      }

      this.clearPaymentErrors()

      try {
        this.isPaymentReady = false

        const result = await this.paymentStageRef.updateCreditAmount(0)

        if (!result.success) {
          const errorMessage =
            result.error ||
            'Payment processing error. Please contact customer support.'
          this.handlePaymentError({ message: errorMessage })
        } else {
          this.lastSubmittedAmount = currentAmount
          this.lastSubmittedSku = currentSku
        }
      } catch (error) {
        console.error('Failed to update payment intent:', error)
        return
      } finally {
        this.isPaymentReady = true
      }
    },
    async processPayment(oneClick: boolean = false) {
      try {
        this.clearPaymentErrors()

        let success: boolean = false
        let result: any = null

        if (oneClick) {
          // For one-click payments, set processing state immediately
          result = await this.paymentStageRef?.processOneClickPayment()
          success = result?.success ?? false
        } else {
          result = await this.paymentStageRef?.processPayment()
          success = result?.submitted ?? false
        }

        if (!success) {
          const errorMessage =
            result.error ||
            result.errorMessage ||
            `${result?.timedOut ? 'Payment processing timed out.' : 'An unexpected error occurred.'} Please contact customer support.`

          this.handlePaymentError({ message: errorMessage })
          this.isPaymentProcessing = false
        }
      } catch (e: any) {
        this.handlePaymentError({
          message:
            e.message ?? 'An unexpected error occurred. Please try again.',
        })
        this.isPaymentProcessing = false
      }
    },
    addToBasketValue(amountInPounds: number) {
      const currentPounds = Math.floor(this.selectedDeposit.amount / 100)
      const newPounds = currentPounds + amountInPounds
      this.pendingCustomAmount = newPounds.toString()
      void this.applyCustomAmount()
    },
    async applyCustomAmount() {
      const pounds = parseInt(this.pendingCustomAmount, 10)

      if (isNaN(pounds) || pounds < 1) {
        return
      }

      const pence = pounds * 100

      if (pence === this.selectedDeposit.amount) {
        return
      }

      if (pence < this.minDepositAmount) {
        this.handlePaymentError({
          message: `The minimum deposit amount is ${this.formatCurrencyFromPounds(this.minDepositPounds)}.<br>Please enter a higher amount.`,
        })
        return
      }

      if (pence > this.maxDepositAmount) {
        this.handlePaymentError({
          message: `The maximum deposit amount is ${this.formatCurrencyFromPounds(this.maxDepositPounds)}.<br>Please enter a lower amount.`,
        })
        return
      }

      const promoOption = this.promotionDepositOption

      const lowestBundle = Math.min(
        ...this.visibleDepositOptions
          .filter(
            (opt: DepositBonusOption) =>
              opt.type === 'catalog' && opt.price_minor
          )
          .map((opt: DepositBonusOption) => opt.price_minor ?? Infinity)
      )

      const maxPromoDeposit =
        promoOption?.promotion_details?.max_deposit_minor ??
        promoOption?.max_deposit_minor

      const promoUpperBound =
        promoOption && Number.isFinite(lowestBundle) && maxPromoDeposit
          ? Math.min(maxPromoDeposit, lowestBundle - 1)
          : maxPromoDeposit

      if (
        promoOption &&
        promoOption.min_deposit_minor !== undefined &&
        promoUpperBound !== undefined &&
        pence >= promoOption.min_deposit_minor &&
        pence <= promoUpperBound
      ) {
        await this.addToBasket(promoOption.sku, pence)
        return
      }

      const walletSku = this.customWalletOption?.sku
      if (walletSku) {
        await this.addToBasket(walletSku, pence)
      }
    },
    handleCustomAmountFocus() {
      this.isCustomAmountFocused = true
      this.selectedOptionSku = this.customWalletOption?.sku ?? null
    },
    handleCustomAmountBlur() {
      if (this.pendingCustomAmount) {
        void this.applyCustomAmount()
      }
      this.isCustomAmountFocused = false
    },
    async addToBasket(depositSku: TProductSku, amount: Pence = 0) {
      amount = Math.round(amount)

      const maxAmount = this.maxDepositAmount
      if (amount > maxAmount) {
        this.handlePaymentError({
          message: `The maximum deposit amount is ${this.formatCurrencyFromPence(maxAmount)}.<br>Please select a lower amount.`,
        })
        return
      }

      this.selectedOptionSku = depositSku

      let deposit: Deposit | null = null

      if (this.depositAmounts.has(depositSku)) {
        const option = this.depositAmounts.get(depositSku) as DepositBonusOption
        deposit = this.createDepositFromOption(option, amount)
      } else {
        for (const depositOption of this.depositAmounts.values()) {
          if (depositOption.price_minor === amount) {
            deposit = this.createDepositFromOption(depositOption, amount)
          }
        }
      }

      if (!deposit) {
        const walletSku = this.customWalletOption?.sku
        deposit = {
          __type: 'Deposit',
          sku: walletSku ?? depositSku,
          amount: amount,
          bonus_credit_percent: calculateBonusCredit(
            amount,
            this.customWalletOption,
            this.depositAmounts
          ),
        }
      }

      const minAmount = this.minDepositAmount
      if (deposit.amount < minAmount) {
        deposit.amount = minAmount
      }

      this.checkoutBasket.clearBasket()
      this.checkoutBasket.addItem(deposit)
      this.selectedAmount = (deposit.amount / 100).toFixed(2)

      this.selectedAmount = Math.floor(deposit.amount / 100).toString()

      const amountInPounds = Math.floor(deposit.amount / 100)
      this.pendingCustomAmount = amountInPounds.toString()

      this.isPaymentReady = true
      await this.debouncedUpdatePayment?.()
    },
  },
})
</script>

<template>
  <AppModal
    id="deposit-checkout-modal"
    button-class="w-full"
    close-button-class="right-6 top-5 z-10"
    class="max-w-[585px] md:w-[585px] w-full"
    :allow-dismissal="!isPaymentProcessing"
    :dialog-mode="false"
    :disable-close="isOneClickPayOpen"
    @close="isOneClickPayOpen = false"
  >
    <template #button="{ openModal }">
      <slot
        name="button"
        :open-modal="() => handleOpenSheetOrModal(openModal)"
        :has-any-deposit-options="hasAnyDepositOptions"
      >
        <AppButton
          class="button-primary !py-0 text-sm !leading-none px-2 md:px-5"
          title="Deposit"
          :disabled="!hasAnyDepositOptions"
          @click="handleOpenSheetOrModal(openModal)"
        >
          Deposit
        </AppButton>
      </slot>
    </template>

    <template #default="{ closeModal }">
      <!-- Payment Processing -->
      <div
        v-show="isPaymentProcessing"
        class="flex w-full flex-col items-center justify-center rounded-lg border border-slate-300 bg-pure-white p-6"
      >
        <div class="spinner-2 size-12"></div>
        <div class="mt-8 text-center text-2xl font-bold">
          Your order is being processed...
          <p class="text-sm text-gray-500">
            Please do not refresh or close this page.
          </p>
        </div>
      </div>

      <!-- Payment Step -->
      <div
        v-if="stepper.current.value === 'payment'"
        v-show="!isPaymentProcessing"
        class="flex flex-col gap-4 rounded-lg border border-slate-300 bg-pure-white p-6"
      >
        <template v-if="!isOneClickPayOpen">
          <h1 class="text-xl font-bold">Deposit</h1>

          <p class="font-bold">Select Amount</p>

          <div class="flex flex-wrap gap-3">
            <DepositOptionCard
              v-for="depositOption in depositOptionsWithCoinCount"
              :key="depositOption.sku"
              :option="depositOption"
              :is-selected="isOptionSelected(depositOption)"
              @select="
                addToBasket(
                  depositOption.sku,
                  depositOption.type === 'promotion'
                    ? (depositOption.promotion_details?.default_deposit_minor ??
                        depositOption.max_deposit_minor ??
                        0)
                    : 0
                )
              "
            />
          </div>

          <!-- Personalised Amount Card -->
          <PersonalisedAmountCard
            :custom-wallet-option="customWalletOption"
            :is-selected="selectedOptionSku === customWalletOption?.sku"
            :pending-amount="pendingCustomAmount"
            :min-deposit-pounds="minDepositPounds"
            :max-deposit-pounds="maxDepositPounds"
            :min-deposit-amount="minDepositAmount"
            :max-deposit-amount="maxDepositAmount"
            :is-payment-updating="isPaymentUpdating"
            :selected-deposit="selectedDeposit"
            :deposit-amounts="depositAmounts"
            :deposit-options-with-coin-count="depositOptionsWithCoinCount"
            :selected-coin-config="selectedCoinConfig"
            @select="
              () => {
                selectedOptionSku = customWalletOption?.sku ?? null
                handleCustomAmountFocus()
              }
            "
            @update:pending-amount="pendingCustomAmount = $event"
            @add-value="addToBasketValue"
          />

          <p class="text-sm">
            For legal reasons 100% of deposit and bonus must be spent on the
            site before withdrawal.
          </p>

          <!-- Bonus tier messaging (visible for all deposit types) -->
          <div v-if="showNextTierHint" class="text-sm text-center mt-2">
            <p class="font-bold text-blue-800">
              Deposit
              {{ formatCurrencyFromPence(bonusTierInfo.remainingPence) }}
              more for next bonus tier and unlock
              {{ $n(bonusTierInfo.nextBonusPercent, 'percent') }} credit!
            </p>
          </div>
        </template>

        <!-- Payment Methods -->
        <PaymentMethodsPanel
          :payment-errors="paymentErrors"
          :is-payment-ready="isPaymentReady"
          :is-payment-processing="isPaymentProcessing"
          :is-add-new-card-open="isAddNewCardOpen"
          :is-one-click-pay-open="isOneClickPayOpen"
          :selected-deposit="selectedDeposit"
          :payment-form="paymentForm"
          :apm-config="apmConfig"
          :one-click-pay="oneClickPay"
          :show-add-new-card="showAddNewCard"
          :success-bonus-pence="successBonusPence"
          @update:is-add-new-card-open="isAddNewCardOpen = $event"
          @update:is-one-click-pay-open="isOneClickPayOpen = $event"
          @clear-payment-errors="clearPaymentErrors"
          @payment-authorized="handlePaymentResult"
          @payment-error="handlePaymentError($event)"
          @payment-processing="isPaymentProcessing = $event"
          @payment-intent-updated="paymentIntentData = $event"
        >
          <template #apmButtons>
            <div
              v-show="isPaymentReady && !isOneClickPayOpen"
              class="flex gap-2 min-h-[40px]"
            >
              <div ref="applePayButton" class="flex-1 h-[40px]" />
              <div ref="googlePayButton" class="flex-1 h-[40px]" />
            </div>

            <!-- Loading state -->
            <p
              v-if="!isPaymentReady"
              class="italic w-full text-center text-sm text-gray-500 min-h-[40px]"
            >
              Loading Payment Options...
            </p>
          </template>

          <template #paymentForm>
            <PaymentStageSolidgate
              v-if="$userStore.user"
              v-show="showAddNewCard || showOneClickPay"
              ref="paymentStageRef"
              :model-value="paymentForm"
              class="payment-stage-solidgate"
              :show-errors="false"
              :payment-errors="paymentErrors"
              :product="selectedDeposit"
              :form-valid="true"
              :form-type="paymentFormType"
              :apm-config="apmConfig"
              :auto-update-intent="false"
              :can-one-click="true"
              @payment-authorized="handlePaymentResult"
              @payment-error="handlePaymentError($event)"
              @payment-processing="isPaymentProcessing = $event"
              @payment-intent:updated="paymentIntentData = $event"
            >
              <template #optin>
                <AppButton
                  id="deposit-bonus-button"
                  class="button-primary min-w-full !leading-5 mt-3 !font-black"
                  :title="paymentButtonLabel"
                  :disabled="
                    !isPaymentReady || isPaymentProcessing || isPaymentUpdating
                  "
                  @click="processPayment(showOneClickPay)"
                >
                  {{ paymentButtonLabel }}
                  <span v-if="oneClickPay && showOneClickPay">
                    | <PaymentCardIcon :brand="oneClickPay.brand" /> ••••
                    <span class="font-black">{{
                      oneClickPay.last_4_digits
                    }}</span>
                  </span>
                </AppButton>
              </template>
            </PaymentStageSolidgate>
          </template>
        </PaymentMethodsPanel>

        <span id="deposit-modal-bottom-anchor" />
      </div>

      <!-- Success Step -->
      <div
        v-if="stepper.current.value === 'success'"
        v-show="!isPaymentProcessing"
        class="flex flex-col justify-center gap-4 rounded-lg border border-slate-300 bg-pure-white p-6"
      >
        <h1 class="text-2xl font-bold">Payment Confirmed</h1>

        <p class="font-bold text-center w-full">
          You Have Deposited
          {{ formatCurrencyFromPence(selectedDeposit.amount) }}
        </p>

        <img
          src="@/assets/images/successful-payment.png"
          alt="Payment Success Illustration"
        />

        <hr />

        <div class="flex border-b flex-col border-b-slate-200">
          <table class="w-full">
            <tbody>
              <tr>
                <td class="pb-3 pr-3 text-left">Deposit Amount:</td>
                <td class="pb-3 text-right font-bold">
                  {{ formatCurrencyFromPence(selectedDeposit.amount) }}
                </td>
              </tr>
              <tr>
                <td class="pb-3 pr-3 text-left">{{ BUSINESS_NAME }} Credit:</td>
                <td class="pb-3 text-right font-bold">
                  {{ formatCurrencyFromPence(successBonusPence) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <AppButton
          class="min-w-full font-bold !leading-none"
          title="Dismiss"
          @click="
            () => {
              closeModal(true)
              void runPendingRefresh()
            }
          "
        >
          Dismiss
        </AppButton>
      </div>
    </template>
  </AppModal>
</template>

<style lang="scss" scoped>
[id^='apple-pay-button-'],
[id^='google-pay-button-'] {
  @apply w-full !important;

  &:empty {
    @apply hidden;
  }

  > * {
    @apply w-full !important;
  }
}

.payment-stage-solidgate {
  :deep(.payment-container) {
    @apply border border-slate-300 bg-primary-lighter p-3 rounded-lg;
  }
}
</style>
